import React, { useState, useEffect } from "react";
import { FiSearch, FiMapPin, FiStar, FiMapPin as FiPin } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import {
  getPublicClasses,
  getPublicActivities,
  getPublicEvents,
} from "../../../services/publicService";
import { resolvePartnerImg } from "../../../utils/partnerImage";

const CARD_LIMIT = 6;

const toBanner = (path) => resolvePartnerImg(path) || "/assets/images/Mask Group 76.webp";

const normalise = {
  class:    (c) => ({ id: `class-${c.id}`,    rawId: c.id, type: "class",    title: c.name || c.title, banner: toBanner(c.main_banner), location: [c.city, c.state].filter(Boolean).join(", "), school: c.school_name, date: c.start_date }),
  activity: (a) => ({ id: `activity-${a.id}`, rawId: a.id, type: "activity", title: a.name,             banner: toBanner(a.main_banner), location: [a.city, a.state].filter(Boolean).join(", "), school: a.school_name, date: a.start_date }),
  event:    (e) => ({ id: `event-${e.id}`,    rawId: e.id, type: "event",    title: e.name,             banner: toBanner(e.main_banner), location: [e.city, e.state].filter(Boolean).join(", "), school: e.school_name, date: e.date || e.start_date }),
};

// Remove duplicate records that the API sometimes returns (e.g. from JOINs)
const dedup = (arr) => {
  const seen = new Set();
  return arr.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

export default function PopularListings() {
  const navigate = useNavigate();
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [location, setLocation] = useState("");

  const fetchItems = async (searchVal = "", locationVal = "") => {
    setLoading(true);
    try {
      const apiFilters = {};
      if (searchVal.trim())   apiFilters.search   = searchVal.trim();
      if (locationVal.trim()) apiFilters.location = locationVal.trim();

      const [clsRes, actRes, evtRes] = await Promise.allSettled([
        getPublicClasses(apiFilters),
        getPublicActivities(apiFilters),
        getPublicEvents(apiFilters),
      ]);

      const classes    = dedup(clsRes.status === "fulfilled" ? (clsRes.value.data?.classes    || []).map(normalise.class)    : []);
      const activities = dedup(actRes.status === "fulfilled" ? (actRes.value.data?.activities || []).map(normalise.activity) : []);
      const events     = dedup(evtRes.status === "fulfilled" ? (evtRes.value.data?.events     || []).map(normalise.event)    : []);

      // interleave types so results aren't all-classes or all-events
      const interleaved = [];
      const maxLen = Math.max(classes.length, activities.length, events.length);
      for (let i = 0; i < maxLen && interleaved.length < CARD_LIMIT; i++) {
        if (classes[i])    interleaved.push(classes[i]);
        if (activities[i]) interleaved.push(activities[i]);
        if (events[i])     interleaved.push(events[i]);
      }
      setItems(interleaved.slice(0, CARD_LIMIT));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSearch = () => fetchItems(search, location);

  const handleViewAll = () => {
    const params = new URLSearchParams();
    if (search.trim())   params.set("search",   search.trim());
    if (location.trim()) params.set("location", location.trim());
    navigate(`/activities${params.toString() ? `?${params}` : ""}`);
  };

  const handleCardClick = (item) => {
    if (item.type === "class")    navigate(`/class-details?id=${item.rawId}`);
    if (item.type === "activity") navigate(`/activity-details?id=${item.rawId}`);
    if (item.type === "event")    navigate(`/event-details?id=${item.rawId}`);
  };

  return (
    <section className="search-section">
      <h2 className="search-title">Popular Classes, Activities &amp; Camps Near You</h2>

      <div className="search-bar">
        <div className="search-input">
          <FiSearch className="icon" />
          <input
            type="text"
            placeholder="Search for Activities, Events etc"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </div>

        <div className="location-input">
          <FiMapPin className="icon" />
          <input
            type="text"
            placeholder="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </div>

        <button className="search-btn" onClick={handleSearch}>Search</button>
      </div>

      {/*
        This used to be a three-way ternary (loading text / "no listings" text /
        populated grid+Swiper fragment). `loading` always starts `true` and `items`
        always starts [] on a fresh client mount, but react-snap's prerender snapshot
        was captured once the fetch resolved (loading=false, items populated) — so the
        static HTML has the full grid+Swiper markup while React's first hydration pass
        would have rendered a single small "Loading listings…" div in its place. That
        top-level element-type swap is a structural hydration mismatch (React error
        #418). Fix: always mount the same status-message div (text only toggles) and
        always mount the grid/Swiper wrapper divs, even while items is still empty.
      */}
      <div style={{ textAlign: "center", padding: items.length === 0 ? "40px 0" : "0", color: "#aaa", fontSize: 14 }} suppressHydrationWarning>
        {items.length === 0 ? (loading ? "Loading listings…" : "No listings found.") : ""}
      </div>
      <>
        {/* Desktop grid — hp-desktop hides it on mobile */}
        <div className="events-sections hp-desktop">
            {items.map((item, idx) => {
              const dateObj = item.date ? new Date(item.date) : null;
              return (
                <div
                  key={idx}
                  className="event-cards"
                  onClick={() => handleCardClick(item)}
                  style={{ cursor: "pointer" }}
                >
                  <div
                    className="event-image"
                    style={{ backgroundImage: `url('${item.banner}')` }}
                  >
                    {dateObj && (
                      <div className="event-date">
                        <div className="event-date-inner">
                          <span className="date-day">{dateObj.getDate()}</span>
                          <span className="date-month">
                            {dateObj.toLocaleString("en-US", { month: "short" }).toUpperCase()}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="event-favorite">
                      <FiStar />
                    </div>
                  </div>

                  <div className="event-content glass">
                    <h3 className="event-title">{item.title}</h3>
                    {item.school   && <p className="event-location">{item.school}</p>}
                    {item.location && (
                      <p className="event-location" style={{ fontSize: 12, color: "#888" }}>
                        <FiPin size={11} style={{ marginRight: 3 }} />
                        {item.location}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile Swiper carousel (shown on mobile via CSS) */}
          <div className="hp-mobile" style={{ padding: "0 16px 8px" }}>
            <Swiper modules={[Pagination]} spaceBetween={16} slidesPerView={1} pagination={{ clickable: true }}>
              {items.map((item, idx) => {
                const dateObj = item.date ? new Date(item.date) : null;
                return (
                  <SwiperSlide key={idx}>
                    <div
                      className="event-cards"
                      onClick={() => handleCardClick(item)}
                      style={{ cursor: "pointer" }}
                    >
                      <div
                        className="event-image"
                        style={{ backgroundImage: `url('${item.banner}')` }}
                      >
                        {dateObj && (
                          <div className="event-date">
                            <div className="event-date-inner">
                              <span className="date-day">{dateObj.getDate()}</span>
                              <span className="date-month">
                                {dateObj.toLocaleString("en-US", { month: "short" }).toUpperCase()}
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="event-favorite">
                          <FiStar />
                        </div>
                      </div>

                      <div className="event-content glass">
                        <h3 className="event-title">{item.title}</h3>
                        {item.school   && <p className="event-location">{item.school}</p>}
                        {item.location && (
                          <p className="event-location" style={{ fontSize: 12, color: "#888" }}>
                            <FiPin size={11} style={{ marginRight: 3 }} />
                            {item.location}
                          </p>
                        )}
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </>

      <div className="view-all-wrapper">
        <button className="view-all-btn" onClick={handleViewAll}>View All</button>
      </div>
    </section>
  );
}
